import React, { useRef, useState } from 'react';
import { useAdminData } from '../../../contexts/AdminDataContext';
import type { CaseEntry, CaseEntryType } from '../../../types/caseEntry';
import type { Case } from '../../../types/admin';
import { caseEntryTotals, sortEntries } from '../../../utils/caseEntries';
import { estimateKind, estimateTotals, otherEntryTypes } from '../../../utils/caseEstimate';
import { useCaseEntryForm } from './useCaseEntryForm';
import { EntryForm } from './EntryForm';
import { HoursSection } from './HoursSection';
import { CostSection } from './CostSection';
import { CaseSummaryPanel } from './CaseSummaryPanel';
import { EstimateComparison } from './EstimateComparison';

/**
 * Forbrug på sagen: tilbuddet er estimatet (læses kun), sagen er det faktiske
 * forbrug. Tilbuddet ændres aldrig herfra.
 */
export function CaseEntries({
  caseItem
}: {
  caseItem: Case;
}) {
  const {
    updateCase,
    settings,
    quotes
  } = useAdminData();
  const entries = caseItem.entries ?? [];
  const actual = caseEntryTotals(entries);

  // Estimatet hentes fra det tilknyttede tilbud — ellers fra kopien på sagen.
  const quote = caseItem.quoteId ? quotes.find(entry => entry.id === caseItem.quoteId) : undefined;
  const quoteLines = quote?.lines?.length ? quote.lines : caseItem.quoteLines ?? [];
  const hasEstimate = Boolean(caseItem.quoteId) && quoteLines.length > 0;
  const estimate = hasEstimate ? estimateTotals(quoteLines) : null;
  const estimatedHours = hasEstimate ? quoteLines.filter(line => estimateKind(line) === 'Timer') : [];
  const estimatedMaterials = hasEstimate ? quoteLines.filter(line => estimateKind(line) === 'Materialer') : [];
  const estimatedOther = hasEstimate ? quoteLines.filter(line => estimateKind(line) === 'Øvrigt') : [];
  const api = useCaseEntryForm(caseItem.hourlyRate || settings.hourlyRate || '');
  const {
    startEdit,
    reset,
    build,
    editingId,
    chooseType
  } = api;
  const formRef = useRef<HTMLFormElement>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  async function persist(next: CaseEntry[]): Promise<boolean> {
    setSaving(true);
    setSaveError('');
    const result = await updateCase(caseItem.id, {
      entries: next
    });
    setSaving(false);
    if (!result.ok) {
      setSaveError(result.error ?? 'Forbruget kunne ikke gemmes. Prøv igen.');
      return false;
    }
    return true;
  }
  function focusForm() {
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }
  function handleEdit(entry: CaseEntry) {
    startEdit(entry);
    focusForm();
  }
  function handleAdd(type: CaseEntryType) {
    chooseType(type);
    focusForm();
  }
  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (saving) return;
    const existing = editingId ? entries.find(entry => entry.id === editingId) : undefined;
    const entry = build(existing);
    if (!entry) return;
    const next = existing ? entries.map(item => item.id === existing.id ? entry : item) : [...entries, entry];
    if (await persist(next)) reset();
  }
  async function handleDelete(entry: CaseEntry) {
    if (saving) return;
    if (!window.confirm(`Slet "${entry.description}"?`)) return;
    if ((await persist(entries.filter(item => item.id !== entry.id))) && editingId === entry.id) reset();
  }
  const sorted = sortEntries(entries);
  const hourEntries = sorted.filter(entry => entry.type === 'Timer');
  return <section className="rounded-card border border-line bg-white p-6" aria-labelledby="forbrug-titel" data-id="element-89" data-mp="31pehlssec643">
      <h2 id="forbrug-titel" className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-90" data-mp="2wrmst7n1t044">
        Forbrug på sagen
      </h2>
      <p className="mt-1.5 text-sm text-ink-muted" data-id="element-91" data-mp="2n11lmvtb6n9l">
        {hasEstimate ? `Tilbud ${caseItem.quoteNumber ?? ''} er estimatet. Det du registrerer her, er det faktiske forbrug — tilbuddet ændres ikke.` : 'Registrér timer, materialer og andre udgifter undervejs. Det hele kopieres med, når du opretter fakturaen.'}
      </p>

      <div className="mt-5" data-id="element-92" data-mp="306wh2x2m7vn5">
        {estimate ? <EstimateComparison estimate={estimate} actual={actual} data-id="element-93" data-mp="356slgxf85p38" /> : <p className="text-sm text-ink-soft" data-id="element-94" data-mp="354ftdrfr5ycg">Ingen estimerede værdier fra tilbud</p>}
      </div>

      <div className="mt-7 grid gap-6 xl:grid-cols-[1fr_16rem]" data-id="element-95" data-mp="2qbw2bp5vnhbd">
        <div className="min-w-0 space-y-8" data-id="element-96" data-mp="2v94nlymbo1gu">
          <HoursSection estimateLines={estimatedHours} entries={hourEntries} editingId={editingId} onEdit={handleEdit} onDelete={handleDelete} onAdd={() => handleAdd('Timer')} data-id="element-97" data-mp="3iygvmv5wy3hk" />

          <CostSection title="Materialer" addLabel="Tilføj materiale" types={['Materialer']} emptyText="Ingen materialer på sagen endnu." estimateLines={estimatedMaterials} allEntries={entries} editingId={editingId} saving={saving} onPersist={persist} onEdit={handleEdit} onDelete={handleDelete} onAdd={() => handleAdd('Materialer')} data-id="element-98" data-mp="398424dg4ezza" />

          <CostSection title="Øvrigt" addLabel="Tilføj udgift" types={otherEntryTypes} showCategory emptyText="Ingen kørsel, maskiner, bortskaffelse eller andre udgifter endnu." estimateLines={estimatedOther} allEntries={entries} editingId={editingId} saving={saving} onPersist={persist} onEdit={handleEdit} onDelete={handleDelete} onAdd={() => handleAdd('Andet')} data-id="element-99" data-mp="1q96fwlo2rin5" />

          <EntryForm ref={formRef} api={api} saving={saving} saveError={saveError} onSubmit={handleSubmit} data-id="element-100" data-mp="1h918wcmsmm9i" />
        </div>

        <CaseSummaryPanel estimate={estimate} actual={actual} data-id="element-101" data-mp="2wp8pn3li31f5" />
      </div>
    </section>;
}